import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type SearchVideo = {
  id: string;
  title: string;
  title_es: string | null;
  description: string | null;
  description_es: string | null;
  thumbnail_url: string | null;
  slug: string | null;
  creator_id: string | null;
  category_slugs: string[];
  category_names: string[];
};

export type SearchCreator = {
  id: string;
  slug: string;
  display_name: string;
  profile_image_url: string | null;
};

export type SearchCategory = {
  id: string;
  slug: string;
  name: string;
  name_es: string | null;
  color: string | null;
  display_order: number | null;
};

export function useSearchIndex() {
  return useQuery({
    queryKey: ["search-index"],
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    queryFn: async () => {
      const [videosRes, creatorsRes, catsRes, junctionRes] = await Promise.all([
        supabase
          .from("video_library")
          .select("id,title,title_es,description,description_es,thumbnail_url,slug,creator_id")
          .eq("is_published", true)
          .order("display_order", { ascending: true }),
        supabase
          .from("creators")
          .select("id,slug,display_name,profile_image_url")
          .eq("is_active", true),
        supabase
          .from("video_categories")
          .select("id,slug,name,name_es,color,display_order")
          .order("display_order", { ascending: true }),
        supabase.from("video_library_categories").select("video_id,category_id"),
      ]);

      const categories: SearchCategory[] = (catsRes.data ?? []) as SearchCategory[];
      const catById = new Map(categories.map((c) => [c.id, c]));

      const catsByVideo = new Map<string, SearchCategory[]>();
      for (const j of (junctionRes.data ?? []) as { video_id: string; category_id: string }[]) {
        const cat = catById.get(j.category_id);
        if (!cat) continue;
        const list = catsByVideo.get(j.video_id) ?? [];
        list.push(cat);
        catsByVideo.set(j.video_id, list);
      }

      const videos: SearchVideo[] = ((videosRes.data ?? []) as any[]).map((v) => {
        const cats = catsByVideo.get(v.id) ?? [];
        return {
          ...v,
          category_slugs: cats.map((c) => c.slug),
          category_names: cats.map((c) => c.name),
        } as SearchVideo;
      });

      const creators: SearchCreator[] = (creatorsRes.data ?? []) as SearchCreator[];

      return { videos, creators, categories };
    },
  });
}

export type SearchResults = {
  videos: SearchVideo[];
  creators: SearchCreator[];
  categories: SearchCategory[];
  total: number;
};

export function runSearch(
  index: { videos: SearchVideo[]; creators: SearchCreator[]; categories: SearchCategory[] } | undefined,
  rawQuery: string,
  locale: string,
): SearchResults {
  const empty: SearchResults = { videos: [], creators: [], categories: [], total: 0 };
  if (!index) return empty;
  const q = rawQuery.trim().toLowerCase();
  if (q.length < 2) return empty;

  const isEs = locale === "es";

  const scoreText = (haystack: string | null | undefined): number => {
    if (!haystack) return 0;
    const h = haystack.toLowerCase();
    if (h === q) return 100;
    if (h.startsWith(q)) return 60;
    if (h.includes(q)) return 30;
    return 0;
  };

  const videos = index.videos
    .map((v) => {
      const title = isEs && v.title_es ? v.title_es : v.title;
      const desc = isEs && v.description_es ? v.description_es : v.description;
      const score =
        scoreText(title) * 2 +
        scoreText(v.title) + // fallback lang
        scoreText(desc) * 0.5 +
        scoreText(v.category_names.join(" "));
      return { v, score };
    })
    .filter((r) => r.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 6)
    .map((r) => r.v);

  const creators = index.creators
    .map((c) => ({ c, score: scoreText(c.display_name) }))
    .filter((r) => r.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 3)
    .map((r) => r.c);

  const categories = index.categories
    .map((c) => ({
      c,
      score: Math.max(scoreText(c.name), scoreText(c.name_es)),
    }))
    .filter((r) => r.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 6)
    .map((r) => r.c);

  return { videos, creators, categories, total: videos.length + creators.length + categories.length };
}
